import grapesjs, { Editor, usePlugin } from "grapesjs";
import GjsEditor, { WithEditor } from "@grapesjs/react";
import { useNavigate, useParams } from "react-router-dom";
import "grapesjs/dist/css/grapes.min.css";
import basic_blocks from "grapesjs-blocks-basic";
import { useState } from "react";
import { PageTypes, ROUTES } from "@/App";
import { gobiStory } from "@/plugins/gobi/gobiPlugin";
import { jodAdFields } from "@/plugins/jobAdFields/jobAdFields";
import { talentechFeedPlugin } from "@/plugins/talentechFeed/talentechFeedPlugin";
import { getUserSupportStatus, getUserTenantId } from "@talentech/bff-login";
import { useSelector } from "react-redux";
import EditorLoader from "./EditorLoader";
import EditorSkeleton from "./EditorSkeleton";
import OpenGraphModal from "./modals/OpenGraphModal";
import PublishTemplateModal from "./modals/PublishTemplateModal";
import SelectTemplate from "./modals/SelectTemplate";
import { iconsMapp } from "./utils/blocks";
import { addEditorButtons } from "./utils/buttons";
import useMediaLibrary from "./utils/mediaLibrary";
import { addProperties, addTraits } from "./utils/properties";
import { useStoreManager } from "./utils/storageManager";
import headingPlugin from "@/plugins/heading/heading";
import linkBlock from "@/plugins/linkBlock/linkBlock";
import parserPostCSS from "grapesjs-parser-postcss";
import hero from "@/plugins/hero";
import CustomCssModal from "./modals/CustomCssModal";
import JobItemTemplateModal from "./modals/JobItemTemplateModal";
import leaflet from "@/plugins/leaflet";
import IconPlugin from "@/plugins/icon";
import BoxPlugin from "@/plugins/box";
import customCodePlugin from "grapesjs-custom-code";
import ExternalResourcesModal from "./modals/ExternalResourcesModal";
import { addHandlers } from "./utils/handlers";
import importExport from "@/plugins/import-export";
import FavIconModal from "./modals/FavIconModal";
import { useGetCustomerConfigQuery } from "@/redux/reducer/customers.api";
import { getPublicLinkBase } from "@/shared/publicLinkBase";
import { feedSource, pageDomains } from "./utils/feedSource";
import imageSelectorTrait from "@/plugins/imageSelectorTrait";
import keenSlider from "@/plugins/keenSlider";
import RevisionModal from "./modals/RevisionModal";
import FontSelectorModal from "./modals/FontSelectorModal";
import CustomFontModal from "./modals/CustomFontModal";
import { useGetPagesQuery } from "@/redux/reducer/pages.api";
import {
  Button,
  ButtonColors,
  SplashScreen,
  TalentechSvgImages,
} from "@talentech/components";

const deviceManagerDevices = [
  {
    name: "XXL", // Extra extra large
    width: null,
  },
  {
    name: "XL", // Extra large
    width: "1400px",
  },
  {
    name: "LG", // Large
    width: "1200px",
  },
  {
    name: "MD", // Medium
    width: "992px",
  },
  {
    name: "SM", // Small
    width: "768px",
  },
  {
    name: "XS", // X-Small
    width: "576px",
  },
];

export default function DefaultEditor() {
  const { onStore, remoteStorageOpts } = useStoreManager();

  const { MediaLibraryModal, customMediaLibraryPlugin } = useMediaLibrary();
  const [isLoading, setIsLoading] = useState(true);

  const { type, id } = useParams<{ id: string; type: PageTypes }>();

  const tenantId = useSelector(getUserTenantId);
  const feedTenantId =
    process.env.NODE_ENV === "development" ? "local_tenant_id" : tenantId;

  const { data } = useGetCustomerConfigQuery({ tenantId });

  const isSupport = useSelector(getUserSupportStatus);

  const onEditor = (editor: Editor) => {
    addHandlers(editor);
    remoteStorageOpts(editor);
    addEditorButtons(editor, isSupport);
    addProperties(editor);
    addTraits(editor);
    iconsMapp(editor);
  };

  const getAssignedDomains = () => pageDomains(data?.domains, type === PageTypes.JOB, id);
  const getAssignedDomain = () => getAssignedDomains()[0];

  const { templates } = useGetPagesQuery(
    {},
    {
      selectFromResult: (res) => ({
        templates:
          type === PageTypes.PAGE
            ? res.data?.page_templates || []
            : res.data?.job_templates || [],
      }),
    }
  );

  const currentTemplate = templates.find((template) => template.id === id);
  const navigate = useNavigate();
  if (!currentTemplate) {
    return (
      <SplashScreen
        productName="Career Sites"
        title="Template not found"
        image={TalentechSvgImages.WORK_SEARCHING_MAGNIFY}
        subtitle="The template you are looking for does not exist"
        size="xl"
        isFullPage={false}
        customComponent={
          <Button
            label="Go back"
            onClick={() => navigate(ROUTES.HOME)}
            color={ButtonColors.PRIMARY}
            icon={<i className="fa-sharp fa-light fa-arrow-left me-2" />}
          />
        }
      />
    );
  }

  return (
    <>
      {isLoading && <EditorSkeleton />}
      <GjsEditor
        onReady={() => {
          setIsLoading(false);
        }}
        style={{
          display: `${isLoading ? "none" : "block"}`,
          position: "relative",
          zIndex: 1,
        }}
        grapesjs={grapesjs}
        options={{
          selectorManager: {
            componentFirst: true,
          },
          height: "calc(100vh - 64px)",
          deviceManager: {
            devices: deviceManagerDevices,
          },
          storageManager: {
            type: "remote",
            stepsBeforeSave: 5,
            options: {
              remote: {
                onStore,
                credentials: "include",
              },
            },
          },
          telemetry: false,
          cssIcons: "",
          pluginsOpts: {
            // @ts-ignore
            [basic_blocks]: {
              stylePrefix: "grapes-",
              flexGrid: 1,
              blocks: ["text", "link", "image", "video"],
            },
          },
        }}
        plugins={[
          basic_blocks,
          parserPostCSS,
          usePlugin(importExport),
          usePlugin(IconPlugin),
          usePlugin(BoxPlugin),
          usePlugin(customMediaLibraryPlugin),
          usePlugin(headingPlugin),
          usePlugin(linkBlock),
          usePlugin(gobiStory),
          usePlugin(hero),
          usePlugin(customCodePlugin),
          usePlugin(imageSelectorTrait),
          usePlugin(keenSlider),
          usePlugin(leaflet),
          usePlugin(talentechFeedPlugin, {
            source: feedSource(process.env.API_URL, feedTenantId, getAssignedDomains()),
            domain: getPublicLinkBase(getAssignedDomain()),
            locale: getAssignedDomain()?.language || "en",
          }),

          ...(type === PageTypes.JOB ? [jodAdFields] : []),
        ]}
        onEditor={onEditor}
      >
        <WithEditor>
          <PublishTemplateModal />
          <OpenGraphModal />
          <SelectTemplate
            pageValues={{
              tenantId: feedTenantId,
              domainId: getAssignedDomain()?.domain_id,
              apiUrl: process.env.API_URL,
            }}
          />
          <CustomCssModal />
          <JobItemTemplateModal />
          {MediaLibraryModal}
          <EditorLoader />
          <ExternalResourcesModal />
          <FavIconModal />
          <RevisionModal />
          <FontSelectorModal />
          <CustomFontModal />
        </WithEditor>
      </GjsEditor>
    </>
  );
}
